import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import studentService from '../services/studentService';
import messService from '../services/messService';
import hostelService from '../services/hostelService';
import paymentService from '../services/paymentService';
import employeeService from '../services/employeeService';
import agreementService from '../services/agreementService';
import notificationService from '../services/notificationService';

const DashboardPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [stats, setStats] = useState({
    students: 0,
    messMemberships: 0,
    hostelAdmissions: 0,
    messPayments: 0,
    hostelPayments: 0,
    employees: 0,
    agreements: 0,
    pendingMessLeaves: 0,
    pendingMessBookings: 0,
    pendingHostelLeaves: 0,
    pendingHostelBookings: 0,
    pendingHostelAdmissions: 0,
    pendingPayments: 0,
    pendingRegistrations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarViewDate, setCalendarViewDate] = useState(new Date());

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [students, messMemberships, hostelAdmissions, payments, employees, agreements, messLeaves, hostelLeaves, pendingRegs, notificationData, hostelBookings, messBookings] = await Promise.allSettled([
          studentService.getStudents(),
          messService.getMembershipsAdmin(),
          hostelService.getAdmissions(),
          paymentService.getPaymentsAdmin(),
          employeeService.getEmployees(),
          agreementService.getAgreements(),
          messService.getLeaves(),
          hostelService.getLeaves(),
          studentService.getPendingRegistrations(),
          notificationService.getUnreadCount(),
          hostelService.getBookingRequests(),
          messService.getBookingRequestsAdmin(),
        ]);
        
        setStats({
          students: students.status === 'fulfilled' ? (students.value.students?.length || 0) : 0,
          messMemberships: messMemberships.status === 'fulfilled' ? (messMemberships.value.data?.data?.length || 0) : 0,
          hostelAdmissions: hostelAdmissions.status === 'fulfilled' ? (hostelAdmissions.value.data?.data?.length || 0) : 0,
          messPayments: payments.status === 'fulfilled' ? (payments.value.data?.data?.filter(p => p.payment_type === 'mess')?.length || 0) : 0,
          hostelPayments: payments.status === 'fulfilled' ? (payments.value.data?.data?.filter(p => ['hostel', 'hostel_rent', 'hostel_deposit'].includes(p.payment_type))?.length || 0) : 0,
          employees: employees.status === 'fulfilled' ? (employees.value.data?.data?.length || 0) : 0,
          agreements: agreements.status === 'fulfilled' ? (agreements.value.data?.data?.length || 0) : 0,
          pendingMessLeaves: messLeaves.status === 'fulfilled' ? (messLeaves.value.data?.data?.filter(l => l.status === 'pending')?.length || 0) : 0,
          pendingMessBookings: messBookings.status === 'fulfilled' ? (messBookings.value.data?.data?.filter(b => b.status === 'pending')?.length || 0) : 0,
          pendingHostelLeaves: hostelLeaves.status === 'fulfilled' ? (hostelLeaves.value.data?.data?.filter(l => l.status === 'pending')?.length || 0) : 0,
          pendingHostelBookings: hostelBookings.status === 'fulfilled' ? (hostelBookings.value.data?.data?.filter(b => b.status === 'pending')?.length || 0) : 0,
          pendingHostelAdmissions: hostelAdmissions.status === 'fulfilled' ? (hostelAdmissions.value.data?.data?.filter(a => ['payment_pending_verification', 'documents_submitted', 'payment_completed'].includes(a.admission_status))?.length || 0) : 0,
          pendingPayments: payments.status === 'fulfilled' ? (payments.value.data?.data?.filter(p => p.status === 'pending')?.length || 0) : 0,
          pendingRegistrations: pendingRegs.status === 'fulfilled' ? (pendingRegs.value.pending_registrations?.length || 0) : 0,
        });

        if (notificationData.status === 'fulfilled') {
          setNotificationCount(notificationData.value.unread_count || 0);
        }
      } catch (err) {
        console.error('Error loading stats:', err);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  // Close calendar on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowCalendar(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const buildCalendarDays = (viewDate) => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return { cells, year, month, today };
  };

  const calendarMonthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const navItems = [
    { title: 'Dashboard', path: '/', icon: '📊' },
    { title: 'Registration Approvals', path: '/registration-approvals', icon: '✅', badge: stats.pendingRegistrations },
    { title: 'Students', path: '/students', icon: '👥' },
    { title: 'Mess Management', path: '/mess', icon: '🍽️', badge: stats.pendingMessLeaves + stats.pendingMessBookings },
    { title: 'Hostel Management', path: '/hostel', icon: '🏠', badge: stats.pendingHostelLeaves + stats.pendingHostelBookings + stats.pendingHostelAdmissions },
    { title: 'Payments', path: '/payments', icon: '💰', badge: stats.pendingPayments },
    { title: 'Employee Management', path: '/employee-management', icon: '👔' },
    { title: 'Agreements', path: '/agreements', icon: '📄' },
  ];


  const dashboardCards = [
    { title: 'Pending Registrations', count: stats.pendingRegistrations, icon: '✅', path: '/registration-approvals', color: '#10b981' },
    { title: 'Total Students', count: stats.students, icon: '👥', path: '/students', color: '#84cc16' },
    { title: 'Mess Memberships', count: stats.messMemberships, icon: '🍽️', path: '/mess#memberships', color: '#f59e0b' },
    { title: 'Hostel Admissions', count: stats.hostelAdmissions, icon: '🏠', path: '/hostel#admissions', color: '#3b82f6' },
    { title: 'Mess Payments', count: stats.messPayments, icon: '💰', path: '/payments#mess', color: '#10b981' },
    { title: 'Hostel Payments', count: stats.hostelPayments, icon: '💳', path: '/payments#hostel', color: '#0891b2' },
    { title: 'Employees', count: stats.employees, icon: '👔', path: '/employee-management', color: '#8b5cf6' },
    { title: 'Agreements', count: stats.agreements, icon: '📄', path: '/agreements', color: '#ec4899' },
  ];

  return (
    <div className="dashboard">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo" onClick={() => navigate('/')}>
            <img src="/SaiNiwasPayingGuest.png" alt="Sai Nirvaar Logo" className="logo-icon" />
            <div className="logo-text">
              <h1>Sai Mess</h1>
              <span>& Hostel</span>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <div
              key={item.path}
              className={`nav-item ${item.path === '/' ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-text">{item.title}</span>
              {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
            </div>
          ))}
        </nav>
        
        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="avatar">{user?.first_name?.charAt(0) || 'A'}</div>
            <div className="user-info">
              <span className="user-name">{user?.first_name || user?.username}</span>
              <span className="user-role">Administrator</span>
            </div>
          </div>
          <div className="sidebar-actions" style={{ display: 'flex', gap: '8px' }}>
            <button className="settings-btn-sidebar" onClick={() => navigate('/settings')} title="Settings" style={{ flex: 1 }}>
              ⚙️ Settings
            </button>
            <button className="logout-btn" onClick={handleLogout} style={{ flex: 1 }}>
              🚪 Logout
            </button>
          </div>
        </div>
      </aside>

      <main className="main-content">
        <header className="main-header">
          <div className="header-left">
            <h2>Dashboard</h2>
            <p>Welcome back, <strong>{user?.first_name || user?.username}</strong></p>
          </div>
          <div className="header-right">
            <button
              className="notification-btn"
              onClick={() => navigate('/notifications')}
              title="View Notifications"
            >
              🔔
              {notificationCount > 0 && <span className="notification-badge">{notificationCount}</span>}
            </button>
            <div style={{ position: 'relative' }}>
              <button
                className="date-display-btn"
                onClick={() => setShowCalendar(v => !v)}
                title="Toggle Calendar"
              >
                📅 {(() => {
                  const date = new Date();
                  const day = String(date.getDate()).padStart(2, '0');
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const year = date.getFullYear();
                  return `${day}/${month}/${year}`;
                })()} {new Date().toLocaleDateString('en-US', { weekday: 'long' })}
              </button>
              {showCalendar && (() => {
                const { cells, year, month, today } = buildCalendarDays(calendarViewDate);
                return (
                  <div className="calendar-popup">
                    <div className="calendar-header">
                      <button className="cal-nav-btn" onClick={() => setCalendarViewDate(new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() - 1, 1))}>‹</button>
                      <span className="cal-month-label">{calendarMonthNames[month]} {year}</span>
                      <button className="cal-nav-btn" onClick={() => setCalendarViewDate(new Date(calendarViewDate.getFullYear(), calendarViewDate.getMonth() + 1, 1))}>›</button>
                    </div>
                    <div className="calendar-grid">
                      {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                        <div key={d} className="cal-day-label">{d}</div>
                      ))}
                      {cells.map((day, idx) => {
                        const isToday = day &&
                          today.getDate() === day &&
                          today.getMonth() === month &&
                          today.getFullYear() === year;
                        return (
                          <div key={idx} className={`cal-day${day ? '' : ' cal-day-empty'}${isToday ? ' cal-day-today' : ''}`}>
                            {day || ''}
                          </div>
                        );
                      })}
                    </div>
                    <div className="cal-footer">
                      <button className="cal-today-btn" onClick={() => setCalendarViewDate(new Date())}>Today</button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </header>

        <div className="dashboard-grid">
          {dashboardCards.map((card) => (
            <div
              key={card.path}
              className="dashboard-card"
              onClick={() => navigate(card.path)}
            >
              <div className="card-icon" style={{ background: card.color }}>
                {card.icon}
              </div>
              <div className="card-content">
                <h3>{card.title}</h3>
                <span className="card-count">{loading ? '...' : card.count}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="quick-stats">
          <h3>Quick Overview</h3>
          <div className="stats-row">
            <div className="stat-item">
              <span className="stat-icon">📊</span>
              <div className="stat-info">
                <span className="stat-value">{stats.pendingRegistrations + stats.pendingMessLeaves + stats.pendingHostelLeaves}</span>
                <span className="stat-label">Pending Actions</span>
              </div>
            </div>
            <div className="stat-item">
              <span className="stat-icon">👥</span>
              <div className="stat-info">
                <span className="stat-value">{stats.students}</span>
                <span className="stat-label">Total Students</span>
              </div>
            </div>
            <div className="stat-item">
              <span className="stat-icon">💼</span>
              <div className="stat-info">
                <span className="stat-value">{stats.employees}</span>
                <span className="stat-label">Staff Members</span>
              </div>
            </div>
          </div>
        </div>

        <footer className="dashboard-footer">
          <p>Design and developed by Denebrix Technologies Pvt. Ltd., Nashik. Mob. 8796901315</p>
        </footer>
      </main>

      <style>{`
        .dashboard {
          display: flex;
          min-height: 100vh;
          background: #f5f7fa;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .sidebar {
          width: 260px;
          background: white;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          border-right: 1px solid #e5e7eb;
        }

        .sidebar-header {
          padding: 24px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
        }

        .logo-icon {
          width: 65px;
          height: 65px;
          object-fit: contain;
          border-radius: 8px;
        }

        .logo-text h1 {
          font-size: 18px;
          font-weight: 700;
          margin: 0;
          color: #1f2937;
        }

        .logo-text span {
          font-size: 12px;
          color: #6b7280;
          font-weight: 700;
        }

        .sidebar-nav {
          flex: 1;
          padding: 16px 12px;
          overflow-y: auto;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 8px;
          cursor: pointer;
          margin-bottom: 4px;
          color: #4b5563;
        }

        .nav-item:hover {
          background: #f3f4f6;
        }

        .nav-item.active {
          background: #f7fee7;
          color: #65a30d;
        }

        .nav-icon { font-size: 18px; }
        .nav-text { font-size: 14px; }

        .nav-badge {
          margin-left: auto;
          background: #dc2626;
          color: white;
          font-size: 11px;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 500;
        }

        .sidebar-footer {
          padding: 16px 12px;
          border-top: 1px solid #e5e7eb;
        }

        .user-profile {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          padding: 10px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .avatar {
          width: 36px;
          height: 36px;
          background: #84cc16;
          color: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .user-info { display: flex; flex-direction: column; }
        .user-name { font-size: 13px; font-weight: 500; color: #1f2937; }
        .user-role { font-size: 11px; color: #6b7280; }

        .logout-btn {
          width: 100%;
          padding: 10px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }

        .logout-btn:hover { background: #fee2e2; }

        .sidebar-actions {
          display: flex;
          gap: 8px;
        }

        .settings-btn-sidebar {
          padding: 10px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #16a34a;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .settings-btn-sidebar:hover { 
          background: #dcfce7;
          border-color: #86efac;
        }

        .main-content {
          flex: 1;
          margin-left: 260px;
          padding: 24px 32px;
        }

        .main-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding: 20px 24px;
          background: linear-gradient(135deg, #84cc16 0%, #65a30d 50%, #4d7c0f 100%);
          border-radius: 16px;
          border: none;
          box-shadow: 
            0 10px 25px -5px rgba(101, 163, 13, 0.4),
            0 8px 16px -8px rgba(77, 124, 15, 0.3),
            0 -2px 4px 0px rgba(217, 249, 157, 0.2) inset,
            0 4px 8px -2px rgba(0, 0, 0, 0.15);
          position: relative;
        }

        .main-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%);
          pointer-events: none;
        }

        .header-left h2 {
          font-size: 24px;
          font-weight: 600;
          color: white;
          margin: 0 0 4px 0;
        }

        .header-left p {
          margin: 0;
          color: white;
          font-size: 14px;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 1;
        }

        .settings-btn {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.2);
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          color: white;
          backdrop-filter: blur(4px);
        }

        .settings-btn:hover {
          background: rgba(255, 255, 255, 0.35);
          transform: rotate(90deg);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .date-display-btn {
          font-size: 13px;
          font-weight: 600;
          color: white;
          padding: 8px 14px;
          background: rgba(255,255,255,0.18);
          border: 2px solid rgba(255,255,255,0.3);
          border-radius: 10px;
          cursor: pointer;
          backdrop-filter: blur(4px);
          transition: background 0.2s ease, border-color 0.2s ease;
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          white-space: nowrap;
        }

        .date-display-btn:hover {
          background: rgba(255,255,255,0.28);
          border-color: rgba(255,255,255,0.5);
        }

        .notification-btn {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.2);
          font-size: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          color: white;
          backdrop-filter: blur(4px);
          position: relative;
        }

        .notification-btn:hover {
          background: rgba(255, 255, 255, 0.35);
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .notification-badge {
          position: absolute;
          top: -6px;
          right: -6px;
          background: #dc2626;
          color: white;
          font-size: 10px;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4);
        }

        .calendar-popup {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 280px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px -10px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.08);
          border: 1px solid #e5e7eb;
          z-index: 2000;
          overflow: hidden;
          animation: calFadeIn 0.18s ease;
        }

        @keyframes calFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .calendar-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px 10px;
          background: linear-gradient(135deg, #84cc16 0%, #65a30d 100%);
        }

        .cal-month-label {
          font-size: 14px;
          font-weight: 700;
          color: white;
          letter-spacing: 0.3px;
        }

        .cal-nav-btn {
          background: rgba(255,255,255,0.25);
          border: none;
          color: white;
          font-size: 18px;
          width: 28px;
          height: 28px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          transition: background 0.15s;
        }

        .cal-nav-btn:hover { background: rgba(255,255,255,0.4); }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          padding: 10px 12px 8px;
        }

        .cal-day-label {
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          padding: 4px 0;
          text-transform: uppercase;
        }

        .cal-day {
          text-align: center;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
          padding: 6px 4px;
          border-radius: 8px;
          cursor: default;
        }

        .cal-day:not(.cal-day-empty):hover { background: #f3f4f6; }

        .cal-day-today {
          background: #84cc16 !important;
          color: white !important;
          font-weight: 700;
        }

        .cal-footer {
          padding: 8px 12px 12px;
          display: flex;
          justify-content: center;
          border-top: 1px solid #f3f4f6;
        }

        .cal-today-btn {
          background: #f7fee7;
          border: 1px solid #bbf7d0;
          color: #65a30d;
          font-size: 12px;
          font-weight: 600;
          padding: 5px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.15s;
        }

        .cal-today-btn:hover { background: #dcfce7; }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .dashboard-card {
          background: white;
          padding: 20px;
          border-radius: 16px;
          border: 1px solid rgba(0, 0, 0, 0.04);
          display: flex;
          align-items: center;
          gap: 16px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.06);
        }

        .dashboard-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 10px 20px -2px rgba(132, 204, 22, 0.3), 0 15px 30px -5px rgba(0, 0, 0, 0.1);
          border-color: #84cc16;
        }

        .card-icon {
          width: 48px;
          height: 48px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }

        .card-content h3 {
          font-size: 13px;
          font-weight: 500;
          color: #6b7280;
          margin: 0 0 4px 0;
        }

        .card-count {
          font-size: 28px;
          font-weight: 600;
          color: #1f2937;
        }

        .quick-stats {
          background: white;
          padding: 24px;
          border-radius: 16px;
          border: 1px solid rgba(0, 0, 0, 0.04);
          box-shadow: 0 6px 12px -2px rgba(99, 102, 241, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05);
        }

        .quick-stats h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 20px 0;
        }

        .stats-row {
          display: flex;
          gap: 24px;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: #f9fafb;
          border-radius: 12px;
          flex: 1;
          border: 1px solid rgba(0, 0, 0, 0.04);
          box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.04);
          transition: all 0.3s ease;
        }

        .stat-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 12px -2px rgba(99, 102, 241, 0.15), 0 8px 16px -4px rgba(0, 0, 0, 0.06);
        }

        .stat-icon { font-size: 24px; }
        .stat-info { display: flex; flex-direction: column; }
        .stat-value { font-size: 24px; font-weight: 600; color: #1f2937; }
        .stat-label { font-size: 13px; color: #6b7280; }

        .dashboard-footer {
          margin-top: 32px;
          padding: 20px;
          text-align: center;
          background: #f5f7fa;
          border-top: 1px solid #e5e7eb;
          border-radius: 8px;
          font-size: 12px;
          color: #6b7280;
        }

        .dashboard-footer p {
          margin: 0;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .sidebar { width: 70px; }
          .logo-text, .nav-text, .user-info { display: none; }
          .nav-item { justify-content: center; }
          .main-content { margin-left: 70px; padding: 16px; }
          .stats-row { flex-direction: column; }
        }
      `}</style>
    </div>
  );
};

export default DashboardPage;
